import React, { useState, useRef, useMemo, useCallback, useEffect } from 'react'
import PropTypes from 'prop-types'
import { formatAmounts, encodeText, plural } from '@aytm/app/stats/lib/helpers'

import ADND from '@aytm/lib/adnd/ADND'

import { CODEBOOKS_HELP, SKIPPER_AVAILABLE_HELP } from '@aytm/app/stats/page/sidebar/components/lib/helps'
import {
  DEFAULT_CODEBOOK,
  FORBIDDEN_CODEBOOK_SYMBOLS,
  STORAGE_KEY
} from '@aytm/app/stats/page/sidebar/coding/lib/constants'
import {
  CUSTOM_QUESTION_REG,
  SENTIMENTS,
  DEFAULT_SELECTED_QUESTION,
  DEFAULT_ORDER_BY,
  DEFAULT_PER_PAGE,
  DEFAULT_SEARCH_STATE,
  CODEBOOK_MIN_LENGTH,
  CODEBOOK_MAX_LENGTH,
  TAG_MIN_LENGTH,
  TAG_MAX_LENGTH
} from '@aytm/app/stats/page/comments/lib/constants'
import {
  questionOldIdToId,
  questionPositionToId,
  getTagsList,
  getFilteredQuestions,
  getSentimentsQuestions
} from '@aytm/app/stats/page/comments/lib/utils'
import {
  changeSurveyCodebookState,
  changeSurveyTagState,
  changeSurveyTagsState,
  changeTagState,
  generateSentiments
} from '@aytm/app/stats/page/comments/actions'
import { setApiKey, setStatActionsUrl, setStatUrl } from '@aytm/app/stats/page/comments/apiCalls'

import useIsVisible from '@aytm/lib/hooks/useIsVisible'

import useDrag from '@aytm/app/stats/page/sidebar/coding/hooks/useDrag'
import useEventBus from '@aytm/app/stats/page/sidebar/coding/hooks/useEventBus'
import useEventListener from '@aytm/app/stats/hooks/useEventListener'

import CodingSkipper from '@aytm/app/stats/page/sidebar/coding/CodingSkipper'
import CodingCodebook from '@aytm/app/stats/page/sidebar/coding/CodingCodebook'
import CodingCodebookSentiment from '@aytm/app/stats/page/sidebar/coding/CodingCodebookSentiment'
import InputNew from '@aytm/app/stats/page/sidebar/coding/components/InputNew'

import SidebarPopup from '@aytm/app/stats/page/sidebar/components/SidebarPopup'

import style from '@aytm/app/stats/page/sidebar/coding/styles/CodingPanel.module.scss'

export const DataContext = React.createContext()

let dndTimer = null
let errorTimer = null

const CodingPanel = ({
  mode = 'comments',
  root,
  apiKey,
  statUrl,
  statActionsUrl,
  sentiments = {},
  features = {},
  survey = {},
  tagsData = {},
  reports = [],
  reportId,
  reportParams = {},
  totals = {},
  windowUID,
  isReportVirtual,
  isReportXpert,
  onFetch,
  onTagsFetch,
  onFetchApiKey,
  onModeChange,
  onSentimentsChange
}) => {
  setApiKey(apiKey)
  setStatUrl(statUrl)
  setStatActionsUrl(statActionsUrl)

  const { config, questions, tags, codeBooks = [] } = survey

  let { selectedQuestion } = reportParams
  selectedQuestion ||= DEFAULT_SELECTED_QUESTION

  const [newItem, setNewItem] = useState(null)
  const [newItems, setNewItems] = useState([])

  const [editingItem, setEditingItem] = useState(null)
  const [settingsItem, setSettingsItem] = useState(null)

  const [isProcessing, setIsProcessing] = useState(false)
  const [isSkipperProcessing, setIsSkipperProcessing] = useState(false)

  const [error, setError] = useState({})
  const [message, setMessage] = useState(null)

  const [progress, setProgress] = useState(0)

  const dndComponentRef = useRef(null)
  const dndItemRef = useRef(null)

  const processingCodebook = useRef(null)
  const processingTag = useRef(null)

  const collapsedRefs = useRef({})

  const progressRef = useRef()
  progressRef.current = progress

  const isVisible = useIsVisible(dndComponentRef && dndComponentRef.current ? dndComponentRef : null)

  const isDNDDisabled = useMemo(
    () =>
      !selectedQuestion ||
      selectedQuestion == DEFAULT_SELECTED_QUESTION ||
      !['comments', 'video-responses'].includes(mode) ||
      features.oe_tags_management.disabled,
    [selectedQuestion, mode, features.oe_tags_management.disabled]
  )

  const isSkipperDisabled = useMemo(() => {
    if (
      !selectedQuestion ||
      selectedQuestion == DEFAULT_SELECTED_QUESTION ||
      CUSTOM_QUESTION_REG.test(selectedQuestion)
    )
      return true

    const question = questions.find(q => selectedQuestion == questionPositionToId(q.position, survey))

    const { type } = question || {}
    return null !== question && ['it', 'im', 'ir', 'va', 'vo', 'ed', 'vr', 'cn'].includes(type)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questions, selectedQuestion])

  const isSentimentsDisabled = useMemo(() => {
    const options = getSentimentsQuestions(getFilteredQuestions(questions))
    const getLast = obj => Object.keys(obj).flatMap(k => (0 < Object.keys(obj[k]).length ? getLast(obj[k]) : k))
    const keys = getLast(options)

    const _sentiments = Object.keys(
      (features.ai_assistants_stat_llm_sentiments.disabled ? sentiments : config?.tags?.sentiment) || {}
    ).reduce((acc, cur) => {
      const path = cur.split('.')
      const question = questions.find(q => path[0] == `q${q.position}`)
      if ('hm' == question.type)
        acc[path.slice(0, 2).join('.')] = (
          features.ai_assistants_stat_llm_sentiments.disabled ? sentiments : config.tags.sentiment
        )[cur]
      else acc[cur] = (features.ai_assistants_stat_llm_sentiments.disabled ? sentiments : config.tags.sentiment)[cur]
      return acc
    }, {})
    const _keys = Object.keys(_sentiments).filter(key => false == _sentiments[key])
    return String(keys.sort()) == String(_keys.sort())
  }, [sentiments, config, questions, features.ai_assistants_stat_llm_sentiments])

  const tagsList = useMemo(() => {
    const _tags = JSON.parse(JSON.stringify(tags || {}))
    const _codeBooks = JSON.parse(JSON.stringify(codeBooks || []))

    for (const newItem of newItems) {
      if (Object.hasOwn(newItem, 'tag')) {
        _tags[newItem.type].push(newItem.tag)

        const codebookIndex = _codeBooks.findIndex(codebook => newItem.codebook == codebook.title)
        if (0 <= codebookIndex) _codeBooks[codebookIndex].tags.push(newItem.label)
      }
    }

    return getTagsList(_tags || {}, {
      codeBooks: _codeBooks,
      cs: tagsData.cs,
      tagsCounters: tagsData.tags,
      tagsStats: tagsData.tagsStats,
      selectedQuestion,
      isSentimentsManagementDisabled: features.oe_sentiments_view.disabled
    })
  }, [tags, codeBooks, newItems, tagsData, selectedQuestion, features.oe_sentiments_view.disabled])

  const tagsCodebooks = useMemo(() => {
    let codebooks = []
    const collapsedStorage = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || '{}')

    if (!features.oe_sentiments_view.disabled) {
      collapsedRefs.current['Sentiments'] = Object.hasOwn(collapsedStorage, 'Sentiments')
        ? collapsedStorage['Sentiments']
        : Object.hasOwn(collapsedRefs.current, 'Sentiments')
          ? collapsedRefs.current['Sentiments']
          : true

      codebooks = codebooks.concat([
        {
          codebook: 'Sentiments',
          type: 'sentiment',
          label: 'Sentiments',
          tags: SENTIMENTS.map(tag => tag.value),
          count: tagsList.filter(tag => 'sentiment' == tag.type).reduce((acc, cur) => acc + (cur.amount || 0), 0),
          color: '#414141',
          disabled: true
        }
      ])
    }

    if (!features.oe_tags_view.disabled) {
      const _codeBooks = JSON.parse(JSON.stringify(codeBooks || []))

      for (const newItem of newItems) {
        if (Object.hasOwn(newItem, 'tag')) {
          const codebookIndex = _codeBooks.findIndex(codebook => newItem.codebook == codebook.title)
          if (0 <= codebookIndex) _codeBooks[codebookIndex].tags.push(newItem.label)
        } else {
          _codeBooks.push({
            title: newItem.codebook,
            type: newItem.type,
            tags: [],
            ts: newItem.ts,
            disabled: true
          })
        }
      }

      codebooks = codebooks.concat(
        _codeBooks.map(codebook => {
          collapsedRefs.current[codebook.title] = Object.hasOwn(collapsedStorage, codebook.title)
            ? collapsedStorage[codebook.title]
            : Object.hasOwn(collapsedRefs.current, codebook.title)
              ? collapsedRefs.current[codebook.title]
              : 1 < codeBooks.length || DEFAULT_CODEBOOK.toLowerCase() != codebook.title.toLowerCase()

          const tags = codebook.tags.map(tag => `${codebook.title}: ${tag}`)
          const stat = Object.keys(tagsData.tagsStats || {})
            .reduce((acc, cur) => {
              const items = tagsData.tagsStats[cur][codebook.type] || []
              for (const item of items) {
                if (!tags.includes(item.tag)) continue
                const q = cur.split('.')[0]
                const id = questionOldIdToId(q, survey)

                const index = acc.findIndex(v => v.id == id)
                if (0 > index) acc.push({ id, amount: item.amount, position: Number(q.slice(1)) })
                else acc[index].amount += item.amount
              }
              return acc
            }, [])
            .sort((a, b) => a.position - b.position)

          return {
            codebook: codebook.title,
            type: codebook.type,
            label: codebook.title,
            tags: codebook.tags.filter((v, i, a) => a.indexOf(v) == i),
            count: codebook.tags.reduce(
              (acc, cur) => acc + (tagsList.find(tag => `${codebook.title}: ${cur}` == tag.tag)?.amount || 0),
              0
            ),
            stat,
            color: `#${codebook.color || '414141'}`,
            ts: 'string' == typeof codebook.ts ? new Date(codebook.ts).getTime() : codebook.ts || 0,
            disabled: codebook.disabled
          }
        })
      )
      codebooks.sort((a, b) => a.ts - b.ts)
    }

    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(collapsedRefs.current))

    return codebooks
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questions, newItems, tagsList, codeBooks, features.oe_tags_view.disabled, features.oe_sentiments_view.disabled])

  const { _onTagsFetch, _onCodeBooksFetch } = useEventBus({
    mode,
    survey,
    tagsData,
    request: reportParams,
    onFetch,
    onTagsFetch
  })

  const onError = useCallback(error => {
    errorTimer && clearTimeout(errorTimer)

    if (error && error.message) {
      setError(error)

      errorTimer = setTimeout(() => setError({}), 5000)
    } else setError({})

    return () => errorTimer && clearTimeout(errorTimer)
  }, [])
  const onMessage = useCallback((message = null, item, type = null) => {
    if (null === message) {
      setMessage(null)
    } else
      setMessage({
        text: (1 < message?.length ? message.map((v, i) => (0 < i ? v : `<b>${v}</b>`)) : message).join('<br/>'),
        item,
        type
      })
  }, [])

  const onProgress = useCallback(value => {
    if (Number(value) === value)
      setProgress(
        Math.min(
          100,
          progressRef.current > value ? progressRef.current + 4 / (progressRef.current - value) : value + 0.5
        )
      )
    else setProgress(Math.min(100, value ? progressRef.current + 1 : 0))
  }, [])

  const onCloseDropdown = useCallback(() => setSettingsItem(null), [])

  const onCancelEditing = useCallback(() => {
    setError({})
    setEditingItem(null)
  }, [])

  const onToggleNew = useCallback(
    value => {
      if (!editingItem) {
        setNewItem(value)
        setError({})
      }
    },
    [editingItem]
  )

  const onCollapse = useCallback((codebook, state) => {
    if (null === state) delete collapsedRefs.current[codebook]
    else collapsedRefs.current[codebook] = state

    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(collapsedRefs.current))
  }, [])

  const onCodebookConfirm = useCallback(
    async (action, codebook, newValue = null) => {
      switch (action) {
        case 'update':
        case 'create':
        case 'clone':
        case 'delete': {
          delete codebook.windowUID

          const params = {
            codebook,
            action,
            onFetch: (data, updated) => {
              if ('update' == action) {
                onCollapse(codebook.codebook, null)
              }

              if (['create', 'clone'].includes(action) && data.codebook) {
                onCollapse(data.codebook.title, false)
              }

              if ('delete' == action) {
                onCollapse(codebook.codebook, null)
              }

              _onCodeBooksFetch(data, updated)
            },
            onError: error => {
              if ('update' == action && newValue) {
                onCollapse(newValue, null)
              }

              onError(error)
            },
            setIsLoading: (value, error) => {
              if (!value) {
                processingCodebook.current = null
                setNewItems(newItems.filter(newItem => newItem.codebook != codebook.codebook))

                !error && onToggleNew(null)
              }

              setIsProcessing(value)
            },
            onFetchApiKey
          }

          if ('update' == action && newValue) {
            if (
              codebook.codebook.toLowerCase() != newValue.toLowerCase() &&
              tagsCodebooks.some(item => item.codebook.toLowerCase() == newValue.toLowerCase())
            ) {
              onError({
                codebook: codebook.codebook,
                name: `${action}Error`,
                message: 'A codebook with the same name already exists'
              })
              break
            }
            params.newValue = newValue

            onCollapse(newValue, collapsedRefs.current[codebook.codebook])
          }

          if ('create' == action && null === newItem) {
            const newCodebook = {
              codebook: `${codebook.codebook} ...`,
              type: 'manual',
              label: `${codebook.codebook} ...`,
              tags: [],
              ts: Date.now()
            }
            setNewItems(newItems.concat([newCodebook]))
            onToggleNew(null)
          }

          if ('clone' == action) {
            const newCodebook = {
              codebook: `${codebook.codebook} ...`,
              type: 'manual',
              label: `${codebook.codebook} ...`,
              tags: [],
              ts: codebook.ts + 1
            }
            setNewItems(newItems.concat([newCodebook]))
            onToggleNew(null)
          } else if ('create' != action) {
            processingCodebook.current = codebook
          }

          onCloseDropdown()
          onCancelEditing()

          await changeSurveyCodebookState(params)
          break
        }
        case 'analysis': {
          if ('sentiment' != codebook.type) return

          const params = {
            codebook,
            action,
            onFetch: (data, callback) => {
              onCollapse(codebook.codebook, false)

              onFetch(data, callback)
            },
            onError: error => {
              onMessage(error, codebook.codebook, 'error')
            },
            onProgress,
            setIsLoading: (value, message) => {
              if (!value) {
                onMessage(message, codebook.codebook)
                onProgress(value)

                processingCodebook.current = null
              }

              setIsProcessing(value)
            },
            onFetchApiKey
          }

          processingCodebook.current = codebook

          onMessage()

          onCloseDropdown()
          onCancelEditing()

          await generateSentiments(params)

          break
        }
        default:
          break
      }
    },
    [
      tagsCodebooks,
      newItem,
      newItems,
      _onCodeBooksFetch,
      onError,
      onMessage,
      onProgress,
      onFetch,
      onFetchApiKey,
      onCancelEditing,
      onCloseDropdown,
      onCollapse,
      onToggleNew
    ]
  )

  const onTagConfirm = useCallback(
    async (action, tag, newValue = null) => {
      switch (action) {
        case 'update':
        case 'create':
        case 'clone':
        case 'delete': {
          delete tag.windowUID

          const params = {
            tag,
            action,
            onFetch: (data, updated) => {
              _onTagsFetch(data, updated)
            },
            onError: onError,
            setIsLoading: (value, error) => {
              if (!value) {
                processingTag.current = null
                setNewItems(newItems.filter(newItem => newItem.tag != tag.tag))

                !error && onToggleNew(null)
              }

              setIsProcessing(value)
            },
            onFetchApiKey
          }

          if ('update' == action && newValue) {
            if (
              tag.tag.toLowerCase() != newValue.toLowerCase() &&
              tagsList.some(item => item.tag.toLowerCase() == newValue.toLowerCase())
            ) {
              onError({
                tag: tag.tag,
                codebook: tag.codebook,
                name: `${action}Error`,
                message: 'A code with the same name already exists'
              })
              break
            }
            params.newValue = newValue
          }

          if ('create' == action && null === newItem) {
            const newTag = Object.assign({}, tag)
            setNewItems(newItems.concat([newTag]))
            onToggleNew(null)
          }

          if ('clone' == action) {
            const newTag = Object.assign({}, tag, tag.destination, {
              tag: null !== tag.destination.codebook ? `${tag.destination.codebook}: ${tag.label}` : tag.label,
              value: null !== tag.destination.codebook ? `${tag.destination.codebook}: ${tag.label}` : tag.label,
              amount: 0,
              count: 0,
              countOnPage: 0
            })
            setNewItems(newItems.concat([newTag]))
            onToggleNew(null)
          } else if ('create' != action) {
            processingTag.current = tag
          }

          onCloseDropdown()
          onCancelEditing()

          await changeSurveyTagState(params)
          break
        }
        case 'merge': {
          const params = {
            tag,
            action,
            onFetch,
            onError: onError,
            setIsLoading: value => {
              if (!value) {
                processingTag.current = null
              }

              setIsProcessing(value)
            },
            onFetchApiKey
          }

          params.newValue = newValue

          processingTag.current = tagsList.find(
            item => item.tag.toLowerCase() == `${tag.destination.codebook}: ${tag.label}`.toLowerCase()
          )

          onCloseDropdown()
          onCancelEditing()

          await changeSurveyTagState(params)
          break
        }
        case 'remove_page':
        case 'remove_found': {
          const { query, pageSize, orderBy, orderDirection } = reportParams

          const params = {
            tag,
            action: 'delete',
            onFetch: _onTagsFetch,
            onError: onError,
            setIsLoading: value => {
              if (!value) {
                processingTag.current = null
              }

              setIsProcessing(value)
            },
            onFetchApiKey
          }

          if ('remove_page' == action) {
            params.request = {
              ...DEFAULT_SEARCH_STATE.request,
              query: query || [],
              selectedQuestion: selectedQuestion || DEFAULT_SELECTED_QUESTION,
              pageSize: pageSize || DEFAULT_PER_PAGE,
              orderBy: orderBy || DEFAULT_ORDER_BY,
              orderDirection: orderDirection || 'asc'
            }
            params.responseId = tagsData.cs.map(item => item.response.uid)
          }

          processingTag.current = tag

          onCloseDropdown()
          onCancelEditing()

          await changeTagState(params)
          break
        }
        default:
          break
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      tagsList,
      tagsData.cs,
      reportParams,
      newItem,
      newItems,
      _onTagsFetch,
      onError,
      onFetch,
      onFetchApiKey,
      onCancelEditing,
      onCloseDropdown,
      onToggleNew
    ]
  )

  const onTagsConfirm = useCallback(
    async (action, tags, codebook) => {
      if ('create' != action) return

      const params = {
        tags,
        codebook,
        action,
        onFetch: (data, updated) => {
          _onTagsFetch(data, updated)
        },
        onError: onError,
        setIsLoading: value => {
          if (!value) {
            setNewItems(newItems.filter(newItem => !tags.some(tag => newItem.tag == tag.tag)))
          }

          setIsProcessing(value)
        },
        onFetchApiKey
      }

      const newTags = Object.assign([], tags)
      setNewItems(newItems.concat(newTags))
      onToggleNew(null)

      onCloseDropdown()
      onCancelEditing()

      await changeSurveyTagsState(params)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      tagsList,
      tagsData.cs,
      reportParams,
      newItem,
      newItems,
      _onTagsFetch,
      onError,
      onFetch,
      onFetchApiKey,
      onCancelEditing,
      onCloseDropdown,
      onToggleNew
    ]
  )

  const onAddCodebook = useCallback(
    (value, tags) => {
      const _value = value.trim()
      const newValue = encodeText(_value, false)

      if (_value.length < CODEBOOK_MIN_LENGTH || _value.length > CODEBOOK_MAX_LENGTH) {
        onError({
          codebook: newValue,
          name: 'createError',
          message: `A codebook length should be between ${CODEBOOK_MIN_LENGTH} and ${CODEBOOK_MAX_LENGTH} characters`
        })
      } else if (
        'sentiments' == _value.toLowerCase() ||
        (!Array.isArray(tags) && tagsCodebooks.some(item => newValue.toLowerCase() == item.codebook.toLowerCase()))
      ) {
        onError({
          codebook: newValue,
          name: 'createError',
          message: 'A codebook with the same name already exists'
        })
      } else {
        const codebook = { codebook: newValue, type: 'manual' }
        if (Array.isArray(tags)) {
          codebook.tags = tags
            .filter(tag => tag.trim().length >= TAG_MIN_LENGTH && tag.trim().length <= TAG_MAX_LENGTH)
            .map(tag => encodeText(tag.trim(), false))
        }

        onCodebookConfirm('create', codebook)
      }
    },
    [tagsCodebooks, onError, onCodebookConfirm]
  )

  const onClickOutside = useCallback(() => {
    if (!editingItem) setSettingsItem(null)
  }, [editingItem])

  const onToggleSettings = useCallback(
    (item, type, codebook) => {
      if (!editingItem) {
        setSettingsItem(
          settingsItem && settingsItem.item == item && settingsItem.type == type && settingsItem.codebook === codebook
            ? null
            : { item, type, codebook }
        )

        onToggleNew(null)
      }
    },
    [editingItem, settingsItem, onToggleNew]
  )

  const onKeyDownHandler = useCallback(
    event => {
      if (27 == event.keyCode) {
        onCloseDropdown()
        onCancelEditing()
      }
    },
    [onCancelEditing, onCloseDropdown]
  )

  const onAction = useCallback(
    (action, item) => {
      if (Object.hasOwn(item, 'tag')) {
        dispatchEvent(new CustomEvent('assignMultipleCancel'))
        if ('update' == action) {
          onToggleNew(null)
          setEditingItem(item)
        }
        if ('assign' == action) {
          setSettingsItem(null)
          dispatchEvent(new CustomEvent('assignMultiple', { detail: item }))
        }
      } else {
        if ('update' == action) {
          onToggleNew(null)
          setEditingItem(item)
        }
      }
    },
    [onToggleNew]
  )

  const onDNDStart = ref => {
    dndItemRef.current = ref
  }
  const onDNDEnd = () => {
    dndTimer && clearTimeout(dndTimer)
    dndTimer = setTimeout(() => {
      dndItemRef.current = null
    }, 300)
  }

  const onDNDEnter = () => {
    if (null !== dndItemRef.current) return
    dndComponentRef.current.classList.add(style['component-active'])
  }
  const onDNDLeave = () => {
    if (null !== dndItemRef.current) return
    dndComponentRef.current.classList.remove(style['component-active'])
  }

  const onDNDDrop = useCallback(
    data => {
      if (null !== dndItemRef.current) return

      const item = data.item || data.data
      if (null === item || item.windowUID == windowUID) return

      onCodebookConfirm(item, 'create')
    },
    [windowUID, onCodebookConfirm]
  )

  const dndComponent =
    editingItem || 0 < newItems.length || isProcessing || isDNDDisabled
      ? null
      : ADND.targets
          .data(
            {
              acceptTypes: ['sidebar-codebook']
            },
            { capture: false }
          )
          .onEnter(onDNDEnter)
          .onLeave(onDNDLeave)
          .onDrop(onDNDDrop)

  useEffect(() => {
    if (dndComponent) dndComponent.connect(dndComponentRef.current)

    return () => {
      if (dndComponent) dndComponent.disconnect()
    }
  }, [dndComponent])

  useEffect(() => {
    if (settingsItem) {
      window.addEventListener('keydown', onKeyDownHandler)
    }

    return () => {
      window.removeEventListener('keydown', onKeyDownHandler)
    }
  }, [settingsItem, onKeyDownHandler])

  useEffect(() => {
    if (isVisible) return

    onCloseDropdown()
    onCancelEditing()
  }, [isVisible, onCloseDropdown, onCancelEditing])

  useDrag(root, isVisible)

  useEventListener('codingLoading', event => {
    if (!event.detail) return

    const { codebook, tag, isLoading: eventIsLoading } = event.detail

    if (tag && null === tag.codebook) {
      const defaultCodebook = tagsCodebooks.find(item => DEFAULT_CODEBOOK.toLowerCase() == item.codebook.toLowerCase())
      if (defaultCodebook) {
        const newTag = Object.assign({}, tag, {
          tag: `${defaultCodebook.codebook}: ${tag.label}`,
          value: `${defaultCodebook.codebook}: ${tag.label}`,
          codebook: defaultCodebook.codebook,
          color: defaultCodebook.color,
          amount: 0,
          count: 0,
          countOnPage: 0
        })
        setNewItems(newItems.concat([newTag]))
      } else {
        const newCodebook = {
          codebook: `${DEFAULT_CODEBOOK} ...`,
          type: 'manual',
          label: `${DEFAULT_CODEBOOK} ...`,
          tags: [],
          ts: Date.now()
        }
        setNewItems(newItems.concat([newCodebook]))
      }
      onToggleNew(null)

      onCloseDropdown()
      onCancelEditing()

      onCollapse(DEFAULT_CODEBOOK, false)
    } else {
      processingCodebook.current = codebook || null
      processingTag.current = tag || null

      if (!eventIsLoading) {
        setNewItems([]) // TODO
        onToggleNew(null)
      }

      setIsProcessing(eventIsLoading)
    }
  })

  const dataContextValue = useMemo(() => {
    return {
      mode,
      root,
      survey,
      sentiments,
      features,
      tagsList,
      tagsCodebooks,
      reportId,
      reports,
      windowUID,
      selectedQuestion,
      isSkipperDisabled,
      isSentimentsDisabled,
      onCollapse,
      onError,
      onMessage,
      onAction,
      onCodebookConfirm,
      onTagConfirm,
      onTagsConfirm,
      onClickOutside,
      onCloseDropdown,
      onCancelEditing,
      onToggleNew,
      onToggleSettings,
      onSentimentsChange
    }
  }, [
    mode,
    root,
    survey,
    sentiments,
    features,
    tagsList,
    tagsCodebooks,
    reportId,
    reports,
    windowUID,
    selectedQuestion,
    isSkipperDisabled,
    isSentimentsDisabled,
    onCollapse,
    onError,
    onMessage,
    onAction,
    onCodebookConfirm,
    onTagConfirm,
    onTagsConfirm,
    onClickOutside,
    onCloseDropdown,
    onCancelEditing,
    onToggleNew,
    onToggleSettings,
    onSentimentsChange
  ])

  return (
    <div className={style.component}>
      <DataContext.Provider value={dataContextValue}>
        {features.oe_tags_management.disabled ? null : features.ai_assistants_oe_coding.available ? (
          <>
            <div className={style['component-title']}>Skipper autocoding</div>

            <div className={style['component-content']}>
              <div className={style.row}>
                <div className="p-col">
                  <a
                    className="p-cmoponent p-button p-button-rounded p-button-stat"
                    href={features.ai_assistants_oe_coding.onPackage.upgradeURL}
                    rel="noreferrer"
                  >
                    <i className="icon-aytmfd-skipper" />
                    {features.ai_assistants_oe_coding.onPackage.text.replace(' for ', ' ')}
                  </a>
                </div>
                <SidebarPopup id="unlock-skipper" root={root} help={SKIPPER_AVAILABLE_HELP} />
              </div>
            </div>
          </>
        ) : features.ai_assistants_oe_coding.disabled ? null : (
          <CodingSkipper
            survey={survey}
            isProcessing={isProcessing}
            isSkipperProcessing={isSkipperProcessing}
            setIsProcessing={setIsProcessing}
            setIsSkipperProcessing={setIsSkipperProcessing}
            onFetch={onFetch}
            onFetchApiKey={onFetchApiKey}
          />
        )}

        <div className={style['component-title']}>
          Codebooks
          <SidebarPopup id="coding" root={root} help={CODEBOOKS_HELP} width="300px" />
          {isReportVirtual || isReportXpert ? null : 0 < totals.totalComments && 'comments' != mode ? (
            <div className={`${style['mode-switch-button']} p-right`} onClick={() => onModeChange('comments')}>
              <i className="icon-aytmfd-comments-v2" />
              <span>
                {formatAmounts(totals.totalComments)}
                <br />
                {plural(totals.totalComments, 'verbatim', 'verbatims')}
              </span>
            </div>
          ) : null}
        </div>

        <div ref={dndComponentRef} className={style['component-content']}>
          {0 < tagsCodebooks.length
            ? tagsCodebooks.map((codebook, index) =>
                'sentiment' == codebook.type ? (
                  <CodingCodebookSentiment
                    key={`codebook-${codebook.type}-${codebook.codebook}-${index + 1}`}
                    codebook={codebook}
                    error={error}
                    message={message}
                    progress={progress}
                    processingTag={processingTag}
                    settingsItem={settingsItem}
                    collapsedRefs={collapsedRefs}
                    isProcessing={isProcessing}
                    isCurrentProcessing={
                      isProcessing &&
                      processingCodebook.current &&
                      processingCodebook.current.codebook == codebook.codebook
                    }
                  />
                ) : (
                  <CodingCodebook
                    key={`codebook-${codebook.type}-${codebook.codebook}-${index + 1}`}
                    codebook={codebook}
                    error={error}
                    newItem={newItem}
                    editingItem={editingItem}
                    newItems={newItems}
                    processingTag={processingTag}
                    settingsItem={settingsItem}
                    collapsedRefs={collapsedRefs}
                    isEditing={editingItem && editingItem.codebook == codebook.codebook && !editingItem.tag}
                    isProcessing={isProcessing}
                    isCurrentProcessing={
                      isProcessing &&
                      processingCodebook.current &&
                      processingCodebook.current.codebook == codebook.codebook
                    }
                    isNewProcessing={newItems.some(newItem => newItem.codebook == codebook.codebook && !newItem.tag)}
                    onDNDStart={onDNDStart}
                    onDNDEnd={onDNDEnd}
                  />
                )
              )
            : null}
        </div>
      </DataContext.Provider>

      {features.oe_tags_management.disabled ? null : (
        <>
          <InputNew
            placeholder="New codebook"
            maxLength={CODEBOOK_MAX_LENGTH}
            forbiddenSymbols={FORBIDDEN_CODEBOOK_SYMBOLS}
            error={
              error &&
              error.message &&
              'createError' == error.name &&
              Object.hasOwn(error, 'codebook') &&
              !Object.hasOwn(error, 'tag')
                ? error
                : null
            }
            multiline
            isAdding={'codebook' == newItem}
            isProcessing={isProcessing}
            onAdd={onAddCodebook}
            onToggle={value => onToggleNew(value ? 'codebook' : null)}
          />

          {error && error.message && !Object.hasOwn(error, 'codebook') && !Object.hasOwn(error, 'tag') && (
            <div className={style['component-error']}>{error.message}</div>
          )}
        </>
      )}
    </div>
  )
}

CodingPanel.propTypes = {
  mode: PropTypes.string,
  root: PropTypes.any,
  apiKey: PropTypes.object,
  statUrl: PropTypes.string,
  statActionsUrl: PropTypes.string,
  sentiments: PropTypes.object,
  features: PropTypes.object,
  survey: PropTypes.object,
  tagsData: PropTypes.object,
  question: PropTypes.object,
  reports: PropTypes.array,
  reportId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  reportParams: PropTypes.object,
  totals: PropTypes.object,
  windowUID: PropTypes.string,
  isReportVirtual: PropTypes.bool,
  isReportXpert: PropTypes.bool,
  onFetch: PropTypes.func,
  onTagsFetch: PropTypes.func,
  onFetchApiKey: PropTypes.func,
  onModeChange: PropTypes.func,
  onSentimentsChange: PropTypes.func
}

CodingPanel.displayName = 'CodingPanel'

export default CodingPanel

ADND().connect()
